import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getPackingSlip } from "@/lib/pack-detail.functions";

export const Route = createFileRoute("/_authenticated/app/pack/$orderId/packing-slip")({
  component: PackingSlipPage,
});

function fmtAddr(addr: unknown): string[] {
  if (!addr || typeof addr !== "object") return [];
  const a = addr as Record<string, string | undefined>;
  const lines: string[] = [];
  if (a.name) lines.push(a.name);
  if (a.company) lines.push(a.company);
  if (a.street1 || a.address1) lines.push(a.street1 ?? a.address1 ?? "");
  if (a.street2 || a.address2) lines.push(a.street2 ?? a.address2 ?? "");
  const cityLine = [a.city, a.state ?? a.region, a.zip ?? a.postal_code ?? a.postal].filter(Boolean).join(", ");
  if (cityLine) lines.push(cityLine);
  if (a.country) lines.push(a.country);
  return lines.filter(Boolean);
}

function PackingSlipPage() {
  const { orderId } = Route.useParams();
  const fetchSlip = useServerFn(getPackingSlip);
  const { data, isLoading, error } = useQuery({
    queryKey: ["packing-slip", orderId],
    queryFn: () => fetchSlip({ data: { orderId } }),
  });

  useEffect(() => {
    if (data) {
      const t = setTimeout(() => window.print(), 250);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (isLoading) return <div className="p-8 text-sm text-muted-foreground">Loading packing slip…</div>;
  if (error || !data) return <div className="p-8 text-sm text-destructive">Failed to load packing slip.</div>;

  const { order, lines, skus, customer } = data;
  const skuById = new Map(skus.map((s) => [s.id, s] as const));
  const shipTo = fmtAddr(order.ship_to);

  return (
    <div className="bg-white text-black mx-auto" style={{ width: "8.5in", padding: "0.5in", fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
      <style>{`@media print { @page { size: letter; margin: 0.5in; } .no-print { display: none !important; } body { background: white; } }`}</style>
      <div className="no-print mb-4 flex justify-end gap-2">
        <button onClick={() => window.print()} className="px-3 py-1.5 text-sm border rounded bg-blue-600 text-white">Print</button>
        <button onClick={() => window.close()} className="px-3 py-1.5 text-sm border rounded">Close</button>
      </div>

      <div className="flex items-start justify-between border-b border-black pb-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">PACKING SLIP</h1>
          <div className="text-sm mt-1">{customer?.name ?? ""}</div>
        </div>
        <div className="text-right text-sm">
          <div><span className="text-gray-500">Order #</span> <span className="font-mono font-semibold">{order.order_number}</span></div>
          {order.customer_po && <div><span className="text-gray-500">PO #</span> <span className="font-mono">{order.customer_po}</span></div>}
          <div><span className="text-gray-500">Date</span> {new Date(order.created_at).toLocaleDateString()}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6 mt-5 text-sm">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Ship To</div>
          {shipTo.length === 0 ? <div className="text-gray-400">—</div> : shipTo.map((l, i) => <div key={i}>{l}</div>)}
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Shipping</div>
          <div>{(order.carrier || "—")}{order.service_level ? ` · ${order.service_level}` : ""}</div>
        </div>
      </div>

      <table className="w-full mt-6 text-sm border-collapse">
        <thead>
          <tr className="border-b-2 border-black text-left">
            <th className="py-2 pr-2 font-semibold">SKU</th>
            <th className="py-2 px-2 font-semibold">Description</th>
            <th className="py-2 pl-2 text-right font-semibold w-20">Qty</th>
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 && (
            <tr><td colSpan={3} className="py-4 text-center text-gray-400">No line items.</td></tr>
          )}
          {lines.map((l) => {
            const s = l.sku_id ? skuById.get(l.sku_id) : null;
            return (
              <tr key={l.id} className="border-b border-gray-200">
                <td className="py-2 pr-2 font-mono">{s?.sku_code ?? "—"}</td>
                <td className="py-2 px-2">{s?.name ?? l.description ?? ""}</td>
                <td className="py-2 pl-2 text-right font-mono">{l.qty_ordered}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {order.notes && (
        <div className="mt-6 text-sm">
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-1">Notes</div>
          <div className="whitespace-pre-wrap">{order.notes}</div>
        </div>
      )}

      <div className="mt-10 pt-4 border-t border-gray-300 text-xs text-gray-500 text-center">
        Thank you for your order.
      </div>
    </div>
  );
}